import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MappingService } from '@services/mapping.service';
import { UiService } from '@services/ui.service';
// import { AuthService } from '@services/auth.service';

import { Mapping } from '@classes/mapping/mapping';
import { MappingDependency } from '@classes/mapping/mapping-dependency';
import { MappingStructure } from '@classes/mapping/mapping-structure';
import { MappingTree } from '@classes/mapping/mapping-tree';
import { ExportTypeEnum } from '@enums/export-type.enum';

@Component({
	selector: 'app-tree-mapping',
	templateUrl: './tree-mapping.component.html',
	styleUrls: ['./tree-mapping.component.scss']
})
export class TreeMappingComponent implements OnInit, OnDestroy {
	@Input() mapping: Mapping;
	@Input() depsLabel: string;
	@Input() canEditMapping: boolean;
	@Input() canEditSummary: boolean;
    @Input() canImportMapping = false ;
	@Input() disableExport: boolean;
	@Input() displayValidity: boolean;
	@Input() isSaving = false;
	@Input() isUploading = false;
	@Output() downloadStructure = new EventEmitter<ExportTypeEnum>();
	@Output() uploadStructure = new EventEmitter<File>();
	@Output() mappingSaved = new EventEmitter<Mapping>();

	showSummaryForm: boolean;
	doesShowList = true;
	canSave: boolean;
	editedTree: MappingStructure[];
	isDownloading = false;
	exportType = ExportTypeEnum;
	isImporting = false;
    hasMapping = false;

	private unsubscribeAll = new Subject<void>();

	constructor(
		private mappingSrv: MappingService,
		private uiSrv: UiService
	) {}

	ngOnInit(): void {
        this.hasMapping = this.mapping.structure?.length !== 0;
		this.initSubscriptions();
	}

	ngOnDestroy(): void {
		this.unsubscribeAll.next();
		this.unsubscribeAll.complete();
	}

	onEditSummary(): void {
		this.showSummaryForm = true;
	}

	onEditSummaryFinished(editedMapping: Mapping): void {
		if (editedMapping) {
			this.mapping.label = editedMapping.label;
			this.mapping.description = editedMapping.description;
			this.mapping.isDefault = editedMapping.isDefault;
			this.canSave = true;
		}
		this.showSummaryForm = false;
	}

	onToggleList(): void {
		this.doesShowList = !this.doesShowList;
	}

	onListChanged(depsFromList: MappingDependency[]): void {
		this.mapping.notMapped = depsFromList;
	}

	onSave(): void {
		if (this.editedTree) {
			this.mapping.structure = this.cleanTreeNodes();
			this.editedTree = null;
		}
		this.mappingSaved.emit(this.mapping);
	}

	onExport(exportType: ExportTypeEnum): void {
		this.downloadStructure.emit(exportType);
		this.uiSrv.isDownloadingSubject.next(true);
	}

	toggleImport(): void {
		this.isImporting = !this.isImporting;
	}

	onImport(file: File): void {
		this.uploadStructure.emit(file);
	}

	private cleanTreeNodes(tree?: MappingTree[]): MappingStructure[] {
		if (!tree) {
			tree = this.editedTree[0].children as MappingTree[];
		}

		return tree.map(item => {
			item.children = item.children
				? this.cleanTreeNodes(item.children as MappingTree[])
				: [];
			delete item.isOpen;
			delete item.isEditMode;
			delete item.isVisible;

			return item as MappingStructure;
		});
	}

	private initSubscriptions(): void {
		this.mappingSrv.unmapDependencies
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe(depsToUnmap => {
				const filteredDeps = depsToUnmap.filter(
					d => !this.mapping.notMapped.some(n => n === d)
				);
				this.mapping.notMapped.push(...filteredDeps);
				this.mapping.notMapped = [...this.mapping.notMapped];
			});

		this.mappingSrv.treeChanged.pipe(takeUntil(this.unsubscribeAll)).subscribe(tree => {
			this.editedTree = tree;
			if (tree) {
				this.canSave = true;
			}
		});

		this.uiSrv.isDownloadingSubject.pipe(takeUntil(this.unsubscribeAll)).subscribe(result => {
			this.isDownloading = result;
		});

        this.mappingSrv.structureImported.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
            this.canSave = true;
        });
	}
}
